import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EyeIcon,
} from '@heroicons/react/24/solid';
import { 
  ShieldCheckIcon, 
  ClockIcon, 
  TrashIcon,
  ChatBubbleBottomCenterTextIcon,
  HeartIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { Logo } from '../components/common';
import { SampleBookViewer } from '../components/features/BookViewer';
import TiltedBookMockup from '../components/features/TiltedBookMockup';
import { HowItWorksSection } from '../components/features/HowItWorks';
import * as coupleBook from '../data/sampleBooks/coupleBook';
import * as familyBook from '../data/sampleBooks/familyBook';
import * as groupBook from '../data/sampleBooks/groupBook';

const SAMPLE_BOOKS = {
  couple: {
    meta: coupleBook.sampleBookMeta,
    characters: coupleBook.sampleCharacters,
    chapters: coupleBook.sampleChapters,
    stories: coupleBook.sampleStories,
    coverImage: '/book-example-couple.png',
  },
  family: {
    meta: familyBook.sampleBookMeta,
    characters: familyBook.sampleCharacters,
    chapters: familyBook.sampleChapters,
    stories: familyBook.sampleStories,
    coverImage: '/book-example-family.png',
  },
  group: {
    meta: groupBook.sampleBookMeta,
    characters: groupBook.sampleCharacters,
    chapters: groupBook.sampleChapters,
    stories: groupBook.sampleStories,
    coverImage: '/book-example-friends.png',
  },
};

function FloatingElements() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Coral blob top-left */}
      <div className="absolute -top-20 -left-20 w-80 h-80 bg-rust/10 rounded-full blur-3xl" />
      {/* Amber blob bottom-right */}
      <div className="absolute -bottom-40 -right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl" />
    </div>
  );
}

export default function LandingPage() {
  const [activeSampleBook, setActiveSampleBook] = useState(null);

  const handleToggleSample = (type) => {
    setActiveSampleBook((prev) => (prev === type ? null : type));
  };

  return (
    <div className="min-h-screen">
      {/* Header with Logo */}
      <header className="absolute top-0 left-0 right-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Logo showText className="w-10 h-10" textClassName="text-2xl" />
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-x-hidden">
        <FloatingElements />

        {/* Mobile: books carousel first, then text */}
        <div className="lg:hidden pt-20 pb-12">
          {/* Auto-scrolling book carousel */}
          <div className="overflow-hidden">
            <div className="flex gap-4 w-max animate-carousel hover:pause-animation">
              {/* Duplicate the set for seamless loop */}
              {[0, 1].map((copy) =>
                [
                  { type: 'group', label: 'Friends' },
                  { type: 'family', label: 'Family' },
                  { type: 'couple', label: 'Couple' },
                ].map(({ type, label }, idx) => (
                  <button
                    key={`${type}-${copy}`}
                    onClick={() => handleToggleSample(type)}
                    className="flex-shrink-0 relative w-40 rounded-xl overflow-hidden shadow-book active:scale-[0.97] transition-transform duration-200"
                  >
                    <img
                      src={SAMPLE_BOOKS[type].coverImage}
                      alt={`${label} sample book`}
                      className="w-full aspect-[5/7] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute bottom-0 inset-x-0 p-2.5 flex items-center justify-center gap-1.5">
                      <EyeIcon className="w-3.5 h-3.5 text-white drop-shadow" />
                      <span className="text-white text-[11px] font-semibold drop-shadow">{label}</span>
                    </div>
                    {/* Tap hint on the center book of the first set only */}
                    {copy === 0 && idx === 1 && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none animate-tap-hint">
                        <div className="bg-black/50 backdrop-blur-sm rounded-full p-3">
                          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 7.5V3.75a1.5 1.5 0 0 1 3 0V7.5" />
                            <path d="M18 7.5v-1.5a1.5 1.5 0 0 1 3 0V12a6 6 0 0 1-6 6H9.28a6 6 0 0 1-4.24-1.76l-3.54-3.54a1.5 1.5 0 0 1 2.12-2.12L6 13V3.75a1.5 1.5 0 0 1 3 0V7.5" />
                            <path d="M12 7.5V2.25a1.5 1.5 0 0 1 3 0V7.5" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
          <p className="text-center text-sepia/50 text-xs mt-3 flex items-center justify-center gap-1.5">
            <EyeIcon className="w-3.5 h-3.5" />
            Tap to peek inside
          </p>

          {/* Text content below carousel */}
          <div className="text-center px-6 mt-6">
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-ink leading-tight mb-4">
              Turn your chats into a
              <span className="block text-gradient mt-1">printed memory book</span>
            </h1>

            <p className="text-base text-sepia max-w-md mx-auto mb-8">
              Preserve your group's funniest moments, inside jokes, and shared memories
              in a beautiful keepsake book you'll treasure forever.
            </p>

            <Link
              to="/type"
              className="inline-flex items-center gap-2 btn-primary text-lg px-8 py-4"
            >
              Create my book
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>

            <p className="mt-5 text-sepia flex items-center gap-2 justify-center">
              <span className="text-forest font-semibold">✨ 3 free chapters</span>
              <span className="text-sepia/60">•</span>
              <span>preview before you buy</span>
            </p>
          </div>
        </div>

        {/* Desktop: side-by-side layout (unchanged) */}
        <div className="hidden lg:block max-w-6xl mx-auto px-4 pt-32 pb-24">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-paper mb-8">
                <SparklesIcon className="w-5 h-5 text-gold" />
                <span className="text-sm font-medium text-sepia">Preserve your conversations forever</span>
              </div>

              <h1 className="font-display text-6xl font-bold text-ink leading-tight mb-6">
                Turn your chats into a
                <span className="block text-gradient mt-2">printed memory book</span>
              </h1>

              <p className="text-xl text-sepia max-w-xl mb-10">
                Preserve your group's funniest moments, inside jokes, and shared memories
                in a beautiful keepsake book you'll treasure forever.
              </p>

              <Link
                to="/type"
                className="inline-flex items-center gap-2 btn-primary text-lg px-8 py-4"
              >
                Create my book
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>

              <p className="mt-5 text-sepia flex items-center gap-2">
                <span className="text-forest font-semibold">✨ 3 free chapters</span>
                <span className="text-sepia/60">•</span>
                <span>preview before you buy</span>
              </p>
            </div>

            {/* Desktop fanned books */}
            <div className="flex items-end justify-start relative pb-8 pr-8" style={{ perspective: '800px' }}>
              {[
                { type: 'group', label: 'Friends', rotate: '-10deg', translate: '20px', z: 1, ml: '0', mr: '-8px' },
                { type: 'couple', label: 'Couple', rotate: '0deg', translate: '-16px', z: 3, ml: '0', mr: '0' },
                { type: 'family', label: 'Family', rotate: '9deg', translate: '16px', z: 2, ml: '-8px', mr: '0' },
              ].map(({ type, label, rotate, translate, z, ml, mr }) => (
                <div
                  key={type}
                  style={{ transform: `rotate(${rotate}) translateY(${translate})`, zIndex: z, marginLeft: ml, marginRight: mr }}
                >
                  <button
                    onClick={() => handleToggleSample(type)}
                    className="group/book cursor-pointer flex flex-col items-center transition-transform duration-300 hover:-translate-y-3"
                  >
                    <div className="relative">
                      <TiltedBookMockup
                        coverImage={SAMPLE_BOOKS[type].coverImage}
                        title=""
                        size="lg"
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/book:opacity-100 transition-opacity duration-300 pointer-events-none">
                        <div className="bg-black/50 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2 shadow-lg">
                          <EyeIcon className="w-4 h-4 text-white" />
                          <span className="text-white text-xs font-semibold whitespace-nowrap">Peek inside</span>
                        </div>
                      </div>
                    </div>
                    <span className="mt-1 text-xs font-semibold text-sepia opacity-0 group-hover/book:opacity-100 transition-opacity duration-200">
                      {label}
                    </span>
                  </button>
                </div>
              ))}
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-sepia text-sm whitespace-nowrap animate-[gentle-pulse_3s_ease-in-out_infinite]">
                <EyeIcon className="w-4 h-4" />
                Click to peek inside
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Types Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 relative">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-center mb-4">
            Perfect for every relationship
          </h2>
          <p className="text-sepia text-center mb-12 max-w-xl mx-auto">
            Whether it's your squad, your partner, or your family – every chat has a story worth keeping.
          </p>
          
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="card text-center group hover:shadow-book transition-all duration-300">
              <div className="w-16 h-16 bg-rust/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <UserGroupIcon className="w-8 h-8 text-rust" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-2">Group Memory Book</h3>
              <p className="text-sm text-sepia">Friend groups, work teams, clubs – celebrate your collective memories</p>
            </div>
            
            <div className="card text-center group hover:shadow-book transition-all duration-300">
              <div className="w-16 h-16 bg-rust/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <HeartIcon className="w-8 h-8 text-rust" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-2">Couple Memory Book</h3>
              <p className="text-sm text-sepia">Your love story in messages – perfect for anniversaries</p>
            </div>
            
            <div className="card text-center group hover:shadow-book transition-all duration-300">
              <div className="w-16 h-16 bg-rust/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <ChatBubbleBottomCenterTextIcon className="w-8 h-8 text-rust" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-2">Family Memory Book</h3>
              <p className="text-sm text-sepia">Family group chats are gold – preserve the chaos</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Trust Section */}
      <section className="py-16 bg-amber-50 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 relative">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-center mb-4 text-ink">
            Your privacy matters
          </h2>
          <p className="text-sepia text-center mb-12">
            We take your private conversations seriously.
          </p>

          <div className="grid sm:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ShieldCheckIcon className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="font-semibold mb-2 text-ink">Private Processing</h3>
              <p className="text-sm text-sepia">Your chat is processed securely and never shared</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrashIcon className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="font-semibold mb-2 text-ink">Auto-Delete</h3>
              <p className="text-sm text-sepia">All data automatically deleted after 30 minutes</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="font-semibold mb-2 text-ink">Consent-Based</h3>
              <p className="text-sm text-sepia">You control what gets included</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden">
        <FloatingElements />
        
        <div className="max-w-2xl mx-auto px-4 text-center relative">
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-6">
            Ready to turn your chats into a book?
          </h2>
          <p className="text-sepia mb-8">
            It only takes a few minutes to turn years of conversations into a beautiful keepsake.
          </p>
          {/* Free chapters banner */}
          <div className="mb-8 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-forest to-forest/80 text-white rounded-full shadow-lg">
            <SparklesIcon className="w-5 h-5 text-gold" />
            <span className="font-semibold">Start with 3 free chapters</span>
            <span className="text-white/70">– no commitment</span>
          </div>
          
          <div className="block">
            <Link
              to="/type"
              className="inline-flex items-center gap-2 btn-primary text-lg px-8 py-4"
            >
              Get Started Free
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 border-t border-parchment">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col items-center gap-3">
            <Logo showText textClassName="text-xl" />
            <p className="text-sm text-sepia/60">
              {"Made with ❤️ for your memories"}
            </p>
            <div className="flex items-center gap-4 text-xs text-sepia/50">
              <a href="mailto:support@thechattybook.com" className="hover:text-sepia transition-colors">Contact</a>
              <span>·</span>
              <Link to="/privacy-policy" className="hover:text-sepia transition-colors">Privacy Policy</Link>
              <span>·</span>
              <Link to="/terms" className="hover:text-sepia transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Sample Book Overlay */}
      {activeSampleBook && (
        <SampleBookViewer
          onClose={() => setActiveSampleBook(null)}
          bookMeta={SAMPLE_BOOKS[activeSampleBook].meta}
          bookCharacters={SAMPLE_BOOKS[activeSampleBook].characters}
          bookChapters={SAMPLE_BOOKS[activeSampleBook].chapters}
          bookStories={SAMPLE_BOOKS[activeSampleBook].stories}
          coverImage={SAMPLE_BOOKS[activeSampleBook].coverImage}
        />
      )}
    </div>
  );
}
